import {
  Box,
  BoxProps,
  Button,
  ButtonGroup,
  ButtonGroupProps,
  ButtonProps,
  Tooltip,
  useColorModeValue,
  useFormControl,
  useToken,
} from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { ForwardedRef, forwardRef, ReactNode, useId } from "react";

export type ButtonDefinition<T extends string = string> = {
  id: T;
  label: string | ReactNode;
  isDisabled?: boolean;
  tooltip?: ReactNode | string;
  leftIcon?: ButtonProps["leftIcon"];
};

export type SwitchButtonGroupProps<T extends string = string> = Omit<
  ButtonGroupProps,
  "onChange" | "onFocus" | "onBlur"
> & {
  buttons: Array<ButtonDefinition<T>>;
  showIcon?: "always" | "active" | "never";
  value?: T | null;
  onChange?: (value: T, prevValue: T | null) => void;
  onFocus?: ButtonProps["onFocus"];
  onBlur?: ButtonProps["onBlur"];
  layoutId?: string;
};

function SwitchButtonGroupInner<T extends string = string>(
  {
    buttons,
    value,
    showIcon = "always",
    onChange,
    onFocus,
    onBlur,
    layoutId: layoutIdProp,
    ...rest
  }: SwitchButtonGroupProps<T>,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const formControlProps = useFormControl<HTMLButtonElement>({
    ...rest,
    onFocus,
    onBlur,
  });

  const buttonGroupBg = useColorModeValue("gray.25", "gray.850");
  const buttonGroupBorder = useColorModeValue("gray.100", "gray.700");
  const buttonGroupBorderRaw = useToken("colors", buttonGroupBorder);
  const activeColor = useColorModeValue("gray.900", "gray.50");
  const activeBg = useColorModeValue("white", "gray.750");
  const activeBorderColor = useColorModeValue("gray.200", "gray.650");
  const inactiveColor = useColorModeValue("gray.500", "gray.500");
  const innerBorderShadow = `inset 0 0 0 1px ${buttonGroupBorderRaw}`;

  const MotionBox = motion<BoxProps>(Box);
  const randomId = useId();
  const layoutId = layoutIdProp ?? randomId;

  return (
    <AnimatePresence>
      <ButtonGroup
        as="section"
        bg={buttonGroupBg}
        borderRadius="md"
        boxShadow={innerBorderShadow}
        isolation="isolate"
        spacing={0}
        {...rest}
      >
        {buttons.map(({ id, label, isDisabled, leftIcon, tooltip }, index) => {
          const isActive = value === id;
          return (
            <Tooltip
              key={id}
              isDisabled={!tooltip}
              label={tooltip}
              placement="top"
              hasArrow
            >
              <Button
                ref={index === 0 ? ref : undefined}
                _active={{
                  bg: undefined,
                }}
                _focus={{
                  zIndex: 1,
                }}
                _hover={{
                  bg: undefined,
                  color: activeColor,
                }}
                aria-pressed={isActive}
                color={isActive ? activeColor : inactiveColor}
                isDisabled={isDisabled || formControlProps.disabled}
                leftIcon={
                  leftIcon &&
                  (showIcon === "always" || (showIcon === "active" && isActive))
                    ? leftIcon
                    : undefined
                }
                variant={"ghost"}
                zIndex={isActive ? 0 : 2}
                onBlur={formControlProps.onBlur}
                onClick={() => onChange?.(id, value ?? null)}
                onFocus={formControlProps.onFocus}
              >
                <MotionBox
                  bg={isActive ? activeBg : undefined}
                  border="1px solid"
                  borderColor={activeBorderColor}
                  borderRadius="md"
                  bottom="0"
                  boxShadow="sm"
                  display={isActive ? "block" : "none"}
                  layoutId={isActive ? layoutId : undefined}
                  left="0"
                  pos="absolute"
                  right="0"
                  top="0"
                  transition={
                    { type: "tween", ease: "easeOut", duration: 0.1 } as any
                  }
                  zIndex={-1}
                />
                <Box zIndex={1}>{label}</Box>
              </Button>
            </Tooltip>
          );
        })}
      </ButtonGroup>
    </AnimatePresence>
  );
}

export const SwitchButtonGroup = forwardRef(SwitchButtonGroupInner) as <
  T extends string = string,
>(
  props: SwitchButtonGroupProps<T> & {
    ref?: ForwardedRef<HTMLButtonElement>;
  },
) => ReturnType<typeof SwitchButtonGroupInner>;
